import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addMember, fetchMembers, removeMember } from "../api/auth";
import { useAuth } from "../auth/AuthContext";
import { Button, Empty, ErrorNote, PageHeader, PageShell, Panel } from "../components/Page";

/**
 * Team seats.
 *
 * The notice at the top is load-bearing, not decorative: a seat records a
 * relationship and currently confers no access, so it must be said plainly
 * rather than left for a dealer to discover after inviting a colleague.
 */
export default function MembersPage() {
  const { seller } = useAuth();
  const queryClient = useQueryClient();
  const [accountId, setAccountId] = useState("");
  const [role, setRole] = useState<"MANAGER" | "STAFF">("STAFF");
  const [formError, setFormError] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({ queryKey: ["members"], queryFn: fetchMembers });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ["members"] });

  const add = useMutation({
    mutationFn: () => addMember(accountId.trim(), role),
    onSuccess: () => {
      setAccountId("");
      setFormError(null);
      invalidate();
    },
    onError: (err) => setFormError(err instanceof Error ? err.message : "Could not add that seat"),
  });

  const remove = useMutation({ mutationFn: (id: string) => removeMember(id), onSuccess: invalidate });

  if (error)
    return (
      <PageShell>
        <ErrorNote error={error} />
      </PageShell>
    );

  return (
    <PageShell>
      <PageHeader
        eyebrow="Workspace"
        title="Team"
        description="Colleagues who manage your listings alongside you."
      />

      <div className="mb-4 rounded-r border-l-[3px] border-signal bg-signal-wash px-4 py-3">
        <p className="text-sm leading-relaxed text-ink">
          A seat records who works with you. It does <strong>not</strong> grant access yet — the
          permission model is still to be built, so a colleague you add cannot sign in to this
          panel until then.
        </p>
      </div>

      <div className="space-y-4">
        <Panel title="Add a seat">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              add.mutate();
            }}
            className="grid gap-4 sm:grid-cols-[1fr_auto_auto] sm:items-end"
          >
            <div className="min-w-0">
              <label className="label" htmlFor="accountId">
                Account ID
              </label>
              {/* An account id is a reference code, so mono is correct here. */}
              <input
                id="accountId"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                placeholder="The id they sign in with"
                className="field field-mono"
              />
            </div>

            <div>
              <label className="label" htmlFor="role">
                Role
              </label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value as "MANAGER" | "STAFF")}
                className="field min-w-36"
              >
                <option value="STAFF">Staff</option>
                <option value="MANAGER">Manager</option>
              </select>
            </div>

            <Button type="submit" disabled={add.isPending || accountId.trim().length === 0}>
              {add.isPending ? "Adding…" : "Add seat"}
            </Button>
          </form>

          {formError ? (
            <p
              role="alert"
              className="mt-3 rounded-r border-l-[3px] border-danger bg-danger-wash px-3 py-2 text-sm text-danger"
            >
              {formError}
            </p>
          ) : null}
        </Panel>

        <Panel
          title={data ? `${data.length} seat${data.length === 1 ? "" : "s"}` : "Seats"}
          flush
        >
          {isPending ? (
            <p className="py-10 text-center text-sm text-ink-muted">Loading…</p>
          ) : !data || data.length === 0 ? (
            <Empty title="No seats yet">You are the only member of this seller profile.</Empty>
          ) : (
            <ul className="divide-y divide-line px-4">
              {data.map((member) => {
                const isOwner = member.accountId === seller?.accountId;
                return (
                  <li key={member.id} className="flex items-center justify-between gap-4 py-3">
                    <div className="min-w-0">
                      <p className="truncate font-mono text-[13px] text-ink">{member.accountId}</p>
                      <p className="mt-0.5 text-xs capitalize text-ink-muted">
                        {member.role.toLowerCase()}
                      </p>
                    </div>
                    {isOwner ? (
                      // The owner's seat cannot be removed — that would orphan
                      // the seller profile.
                      <span className="shrink-0 rounded-full bg-primary-wash px-2 py-0.5 text-[11px] font-medium text-primary">
                        you
                      </span>
                    ) : (
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(member.accountId)}
                        className="shrink-0"
                      >
                        Remove
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>
    </PageShell>
  );
}